import React from 'react';

import { Login } from '@mui/icons-material';
import { Link, Stack, Tooltip } from '@mui/material';

import { toggleExpandingStateManually } from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import { ContextualEvents } from './components/ContextualEvents';
import { KibanaLink } from './components/KibanaLink';
import { NetworkRequestBody } from './components/NetworkRequestBody';
import { NetworkRequestKeyValuePairDetails } from './components/NetworkRequestKeyValuePairDetails';
import { LogEntryNetworkCellModel, LogEntryNetworkCellModelData } from './LogEntryNetworkCellModel';
import { NetworkRequestColorCodingTooltipContent } from './NetworkRequestColorCodingTooltipContent';

export class NetworkResponseCell extends React.Component<{ model: LogEntryNetworkCellModel }> {
  render() {
    const model = this.props.model;
    const data = model.data;
    const resData = data.response!;

    let responseCssClassName: string;
    switch (data.outcome) {
      case NetworkRequestOutcome.Unknown:
        responseCssClassName = 'network-response-unknown';
        break;
      case NetworkRequestOutcome.Canceled:
        responseCssClassName = 'network-response-canceled';
        break;
      case NetworkRequestOutcome.Succeeded:
        responseCssClassName = 'network-response-succeeded';
        break;
      case NetworkRequestOutcome.Failed:
        responseCssClassName = 'network-response-failed';
        break;
      case NetworkRequestOutcome.ProbablyFailed:
        responseCssClassName = 'network-response-probably-failed';
        break;
    }

    return (
      <div>
        <Stack direction="row" spacing="8px" alignItems="center">
          <Tooltip
            disableInteractive
            title={<NetworkRequestColorCodingTooltipContent outcome={data.outcome} />}
          >
            <Stack
              direction="row"
              spacing="8px"
              alignItems="center"
              className={responseCssClassName}
            >
              <Login />
              <HighlightableStringView string={resData.title} />
            </Stack>
          </Tooltip>

          {resData.jumpToRequestLink ? (
            <Link
              href=""
              onClick={(event) => {
                if (this.props.model.onRequestJumpToLogEntry) {
                  this.props.model.onRequestJumpToLogEntry(
                    resData.jumpToRequestLink!.requestLogEntryIndex,
                  );
                }
                event.preventDefault();
              }}
            >
              <HighlightableStringView string={resData.jumpToRequestLink.text} />
            </Link>
          ) : null}

          {data.kibanaLink ? (
            <KibanaLink url={data.kibanaLink.url}>
              <HighlightableStringView string={data.kibanaLink.text} />
            </KibanaLink>
          ) : null}
        </Stack>

        {data.url ? <HighlightableStringView string={data.url} /> : null}

        {resData.canceled ? (
          <div>
            <HighlightableStringView string={resData.canceled} />
          </div>
        ) : null}

        {resData.httpStatus ? (
          <div>
            <HighlightableStringView string={resData.httpStatus} />
          </div>
        ) : null}

        {resData.duration ? (
          <div>
            <HighlightableStringView string={resData.duration} />
          </div>
        ) : null}

        {resData.code ? (
          <div>
            <HighlightableStringView string={resData.code} />
          </div>
        ) : null}

        {resData.message ? (
          <div>
            <HighlightableStringView string={resData.message} />
          </div>
        ) : null}

        {resData.additionalInfo ? (
          <div>
            <HighlightableStringView string={resData.additionalInfo} />
          </div>
        ) : null}

        {resData.headers ? (
          <NetworkRequestKeyValuePairDetails
            dataModel={resData.headers}
            onOpenToggle={() => {
              const change: DeepPartial<LogEntryNetworkCellModelData> = {
                response: {
                  headers: {
                    expandingState: toggleExpandingStateManually(resData.headers!.expandingState),
                  },
                },
              };
              model.delegate?.onLogEntryCellModelChange({
                cellModel: model,
                postProcess: false,
                change,
              });
            }}
          />
        ) : null}

        {resData.body ? (
          <NetworkRequestBody
            data={resData.body}
            onDataChange={(bodyChange) => {
              const change: DeepPartial<LogEntryNetworkCellModelData> = {
                response: {
                  body: bodyChange.change,
                },
              };
              this.props.model.delegate?.onLogEntryCellModelChange({
                cellModel: model,
                change,
                postProcess: bodyChange.postProcess,
              });
            }}
            onRequestCreateNewColumn={(jqQuery) => {
              const c = this.props.model.onRequestCreateNewColumn;
              if (c) {
                c(jqQuery);
              }
            }}
          />
        ) : null}

        {this.props.model.onRequestSurroundingEntries && this.props.model.showContextualEvents ? (
          <ContextualEvents
            currentEntryIndex={data.entryIndex}
            currentEntryLabel={`Response #${data.entryIndex + 1}`}
            onRequestSurroundingEntries={this.props.model.onRequestSurroundingEntries}
          />
        ) : null}
      </div>
    );
  }
}
